import { useState, useEffect, useRef } from 'react';

/**
 * BackToTop — three-phase scroll-aware button.
 *
 * The button only appears once the user has scrolled to (or very near)
 * the bottom of the page.  After that, three phases apply:
 *
 *   'hidden'  — not yet reached the bottom, OR back near the top
 *               (scrollY ≤ heroThreshold) → invisible, parked at bottom
 *   'bottom'  — reached the bottom; currently scrolling DOWN (or at rest)
 *               → visible at bottom centre
 *   'top'     — reached the bottom; now scrolling back UP
 *               → slides to top centre, dimmed to ~60 % opacity
 *
 * The base CSS always parks the element at the bottom of the viewport so:
 *   hidden → bottom  =  clean fade-in  (no position jump)
 *   top    → hidden  =  slides back down while fading out
 *
 * Adapts to light / dark theme via [data-theme="dark"] on <html>.
 */
export default function BackToTop({ heroThreshold = 1200, bottomMargin = 100 }) {
  const [phase, setPhase] = useState('hidden');
  const lastY = useRef(window.scrollY);
  // Latches true the first time the user reaches the bottom; resets when
  // they scroll back above heroThreshold so the next visit starts fresh.
  const reachedBottom = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const goingUp = y < lastY.current;
      lastY.current = y;

      // Detect arrival at (or within bottomMargin px of) the page bottom
      const atBottom =
        y + window.innerHeight >=
        document.documentElement.scrollHeight - bottomMargin;

      if (atBottom) {
        reachedBottom.current = true;
      }

      if (y <= heroThreshold) {
        // Back near the top — hide and allow a fresh trigger next time
        reachedBottom.current = false;
        setPhase('hidden');
      } else if (reachedBottom.current) {
        // User has hit the bottom at least once this scroll session
        setPhase(goingUp ? 'top' : 'bottom');
      }
      // else: still scrolling down but hasn't reached bottom yet → stay hidden
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [heroThreshold, bottomMargin]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <button
      id='back-to-top-btn'
      className={`back-to-top back-to-top--${phase}`}
      onClick={scrollToTop}
      aria-label='Back to top'
      title='Back to top'
    >
      <svg
        className='back-to-top__arrow'
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
      >
        <polyline points='18 15 12 9 6 15' />
      </svg>
      <span className='back-to-top__label'>Back to Top</span>
    </button>
  );
}
